import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  buildCalendarMonth,
  calendarEntryTone,
  deadlineCalendarEntries,
  tripCalendarEntries,
  type CalendarEntry,
  type CalendarEntryTone,
  type ProposalDeadlineRadarItem,
  type TripDto,
} from "@tc/shared";
import { Card, IconButton } from "@/components/ui";
import { useClubFilter } from "@/lib/clubs/club-filter";
import { useClubLookup } from "@/lib/clubs/clubs";
import { useFormatters } from "@/lib/i18n/format";
import { tripsApi } from "@/features/trips/api";
import { calendarApi } from "./api";

/** id → club name, or null when names aren't available (the club view has no club list). */
type ClubNameFn = (clubId: string | null | undefined) => string | null;

/**
 * Colour token per entry tone — the three colours the legend explains. Trips ("event") read blue,
 * upcoming deadlines amber, and overdue deadlines red, regardless of trip status.
 */
const CAL_TONE_META: Record<CalendarEntryTone, { c: string; bg: string }> = {
  event: { c: "var(--info)", bg: "var(--info-soft)" },
  deadline: { c: "var(--warning)", bg: "var(--warning-soft)" },
  overdue: { c: "var(--danger)", bg: "var(--danger-soft)" },
};

/** The legend tones, in the order they're shown beneath the grid. */
const CAL_TONES = ["event", "deadline", "overdue"] as const;

/**
 * Resolve an entry's pill colour + label. Colour comes from the entry's tone (event / deadline /
 * overdue); back office prefixes each label with the club name.
 */
function useEntryStyles(todayIso: string, clubName?: ClubNameFn) {
  const { t } = useTranslation();
  return useCallback(
    (entry: CalendarEntry): { color: string; background: string; label: string } => {
      const clubPrefix = clubName ? clubName(entry.clubId) : null;
      const meta = CAL_TONE_META[calendarEntryTone(entry, todayIso)];
      const text =
        entry.kind === "trip"
          ? (entry.destinationCity ?? entry.reference)
          : (entry.label ?? (entry.labelKey ? t(entry.labelKey) : ""));
      return {
        color: meta.c,
        background: meta.bg,
        label: clubPrefix ? `${clubPrefix} · ${text}` : text,
      };
    },
    [clubName, t, todayIso],
  );
}

/**
 * The calendar (TW-3456). A Monday-first month grid plotting the club's submitted trips (on their
 * travel start) and its still-pending proposal deadlines; clicking an entry opens the trip. Both
 * audiences share this view via {@link ClubCalendarPage}/{@link OpsCalendarPage}; back office sees
 * every club (scoped by the ClubFilter) and labels each entry with its club. Trips + deadlines are
 * bucketed by the shared `buildCalendarMonth` fold — the same logic its unit tests pin.
 */
function CalendarView({
  scopeId,
  showClub = false,
  clubName,
}: {
  scopeId?: string;
  showClub?: boolean;
  clubName?: ClubNameFn;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();

  const [trips, setTrips] = useState<TripDto[] | null>(null);
  const [deadlines, setDeadlines] = useState<ProposalDeadlineRadarItem[]>([]);
  const [error, setError] = useState(false);
  const [offset, setOffset] = useState(0);

  // Anchor "now" once per mount so the grid (and its "today" cell) is stable across re-renders.
  const now = useMemo(() => new Date(), []);
  const todayIso = now.toISOString();
  const entryStyles = useEntryStyles(todayIso, clubName);
  const view = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const year = view.getFullYear();
  const month = view.getMonth();

  const load = useCallback(async () => {
    setError(false);
    try {
      const [tripList, deadlineList] = await Promise.all([
        tripsApi.list(scopeId),
        calendarApi.deadlines(scopeId),
      ]);
      setTrips(tripList);
      setDeadlines(deadlineList);
    } catch {
      setError(true);
    }
  }, [scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const grid = useMemo(() => {
    const entries: CalendarEntry[] = [
      ...tripCalendarEntries(trips ?? []),
      ...deadlineCalendarEntries(deadlines),
    ];
    return buildCalendarMonth(year, month, entries, todayIso);
  }, [trips, deadlines, year, month, todayIso]);

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => fmt.date(new Date(2024, 0, 1 + i), { weekday: "narrow" })),
    [fmt],
  );

  return (
    <div className="page">
      <div className="row-between page-head" style={{ alignItems: "center" }}>
        <div>
          <h1>{t("nav.calendar")}</h1>
          <div className="sub">{showClub ? t("calendar.opsSub") : t("calendar.clubSub")}</div>
        </div>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          <IconButton name="chevL" label={t("calendar.prev")} onClick={() => setOffset((o) => o - 1)} />
          <b style={{ minWidth: 150, textAlign: "center", textTransform: "capitalize" }}>
            {fmt.date(view, { month: "long", year: "numeric" })}
          </b>
          <IconButton name="chevR" label={t("calendar.next")} onClick={() => setOffset((o) => o + 1)} />
        </div>
      </div>

      {error ? (
        <div className="empty">{t("calendar.loadError")}</div>
      ) : trips === null ? (
        <div className="empty">{t("common.loading")}</div>
      ) : (
        <Card>
          <div className="cal-grid">
            {weekdays.map((d, i) => (
              <div key={i} className="cal-dow">
                {d}
              </div>
            ))}
            {grid.cells.map((cell, i) => (
              <div key={i} className={`cal-cell${cell.day === null ? " is-pad" : ""}${cell.isToday ? " is-today" : ""}`}>
                {cell.day !== null && <div className="cal-day">{cell.day}</div>}
                {cell.entries.map((entry, j) => {
                  const style = entryStyles(entry);
                  return (
                    <button
                      key={j}
                      type="button"
                      className="cal-pill"
                      style={{ color: style.color, background: style.background }}
                      title={style.label}
                      onClick={() => navigate(`/trips/${entry.tripId}`)}
                    >
                      {style.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="cal-legend">
            {CAL_TONES.map((tone) => (
              <span key={tone} className="cal-legend-item">
                <span
                  className="cal-legend-swatch"
                  style={{
                    background: CAL_TONE_META[tone].bg,
                    borderColor: `color-mix(in oklab, ${CAL_TONE_META[tone].c} 38%, transparent)`,
                  }}
                />
                {t(`calendar.legend.${tone}`)}
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Club calendar — the club's own submitted trips + pending deadlines (RLS-scoped; no switcher). */
export function ClubCalendarPage() {
  return <CalendarView />;
}

/** Back-office calendar — every club's trips + deadlines, scoped by the ClubFilter; labels by club. */
export function OpsCalendarPage() {
  const { scopeId } = useClubFilter();
  const clubs = useClubLookup();
  return <CalendarView scopeId={scopeId} showClub clubName={clubs.name} />;
}
